/** @format */

import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Observable } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { HttpErrorHandler, HandleError } from '../http-error-handler.service';
import { Problem, ProblemDB, NewProblem, ContactDB, ProblemContact, ContactPhone, DuplicateProblemPayload } from '../../models/problem';
import {
	MenuItem,
	ProblemMenuConfig,
	ProblemContactsPayload,
	LinkedCase,
	LinkedCasePayload,
	UpdateLinkedCasePayload,
	UnlinkProblemCasePayload,
	FromProblemLinkedCasePayload,
	LinkedCaseRelated,
	SelectedProblemSubType,
} from '../../state-management';
import { History } from 'src/app/models/history';
import { Attachment, UpdateAttachmentDetails, EdocsDocument } from 'src/app/models/attachment';

// TODO move the catchError to the effect files for error handling

@Injectable()
export class ProblemsService {
	problemsUrl = 'api/problems';
	staticUrl = 'api/static';
	attachmentsUrl = 'api/attachments';
	problemsLinkedUrl = 'api/problemlinked';
	graphMeEndpoint = 'https://graph.microsoft.com/v1.0';
	private handleError: HandleError;

	httpOptions = {
		headers: new HttpHeaders({
			'Cache-Control': 'no-cache',
			Pragma: 'no-cache',
		}),
	};

	constructor(private httpClient: HttpClient, httpErrorHandler: HttpErrorHandler) {
		this.handleError = httpErrorHandler.createHandleError('ProblemsService');
	}

	getProblems(): Observable<Problem[]> {
		return this.httpClient.get<Problem[]>(this.problemsUrl, this.httpOptions);
	}

	getProblem(problemNo: string): Observable<Problem[]> {
		return this.httpClient.get<Problem[]>(this.problemsUrl + '/' + problemNo, this.httpOptions);
	}

	getRecentProblems(): Observable<Problem[]> {
		return this.httpClient.get<Problem[]>(`${this.problemsUrl}/recentCases`, this.httpOptions);
	}

	getDashboardProblems(): Observable<Problem[]> {
		return this.httpClient.get<Problem[]>(this.problemsUrl + '/dashboardproblems', this.httpOptions);
	}

	getProblemContacts(problemNo: string): Observable<ContactDB[]> {
		return this.httpClient.get<ContactDB[]>(this.problemsUrl + '/getProblemContacts/' + problemNo, this.httpOptions);
	}

	heartbeat(): Observable<any[]> {
		return this.httpClient.get<any[]>(this.problemsUrl + '/heartbeat', this.httpOptions);
	}

	getDashboardView(): Observable<string> {
		return this.httpClient.get<string>(this.staticUrl + '/dashboard', this.httpOptions);
	}

	saveDashboardView(setting: string): Observable<string> {
		return this.httpClient.post<string>(this.staticUrl + '/saveDashboard/' + setting, this.httpOptions);
	}

	getMenuOptions(): Observable<MenuItem> {
		return this.httpClient.get<MenuItem>(this.staticUrl + '/menu', this.httpOptions);
	}

	getNewCaseMenu(): Observable<ProblemMenuConfig[]> {
		return this.httpClient
			.get<ProblemMenuConfig[]>(this.problemsUrl + '/newCaseMenuConfig', this.httpOptions)
			.pipe(catchError(this.handleError('getNewCaseMenu', [])));
	}

	// addProblem(problem: ProblemDB): Observable<{ caseNo: string, department: string, user: string, role: string }> {
	//   return this.httpClient.post<ProblemDB>(this.problemsUrl, problem).pipe(
	//     catchError(this.handleError('addProblem', null))
	//   );
	// }

	addProblem(problem: ProblemDB): Observable<any> {
		return this.httpClient.post<any>(this.problemsUrl, problem, this.httpOptions).pipe(catchError(this.handleError('addProblem', null)));
	}

	getAllProblemSubTypes(): Observable<SelectedProblemSubType[]> {
		return this.httpClient.get<SelectedProblemSubType[]>(`${this.problemsUrl}/allSubTypes`, this.httpOptions);
	}

	duplicateProblem(duplicateProblem: DuplicateProblemPayload): Observable<any> {
		return this.httpClient.post<any>(
			`${this.problemsUrl}/duplicateProblem/${duplicateProblem.originalProblemNo}`,
			duplicateProblem,
			this.httpOptions
		);
	}

	closeProblem(problemId: number[], resolution: string): Observable<any> {
		return this.httpClient.post<any>(`${this.problemsUrl}/closeproblems/`, { problemId, resolution }, this.httpOptions);
	}

	addCodeCase(problemNo: string, token: string): Observable<LinkedCase> {
		return this.httpClient.post<LinkedCase>(this.problemsUrl + '/CreateCodeCase/' + problemNo + '/' + token, this.httpOptions);
	}

	addPermit(problemNo: string, permitType: string, token: string): Observable<LinkedCase> {
		return this.httpClient.post<LinkedCase>(this.problemsUrl + '/CreatePermit/' + problemNo + '/' + permitType + '/' + token, this.httpOptions);
	}

	getProblemHistory(problemNo: string): Observable<History[]> {
		return this.httpClient.get<History[]>(this.problemsUrl + '/history/' + problemNo, this.httpOptions);
	}

	getProblemDescriptionHistory(problemNo: string): Observable<History[]> {
		return this.httpClient
			.get<History[]>(this.problemsUrl + '/description-history/' + problemNo, this.httpOptions)
			.pipe(catchError(this.handleError('getProblemDescriptionHistory', [])));
	}

	updateProblem(problemObj: { problemId: number; resolution?: string; problem: NewProblem }): Observable<Problem[]> {
		return this.httpClient.post<Problem[]>(
			this.problemsUrl + '/' + problemObj.problemId + '?resolution=' + problemObj.resolution,
			problemObj.problem,
			this.httpOptions
		);
	}

	addContact(contact: ProblemContactsPayload): Observable<any> {
		return this.httpClient.post<ProblemContact[]>(this.problemsUrl + '/contact/' + contact.problem_Id, contact.contacts, this.httpOptions);
	}

	updateContact(contact: ProblemContact): Observable<any> {
		return this.httpClient.post<ProblemContact>(this.problemsUrl + '/contact/', contact, this.httpOptions);
	}

	getProblemAttachments(problemNo: string): Observable<Attachment[]> {
		return this.httpClient.get<Attachment[]>(this.attachmentsUrl + '/getProblemAttachments/' + problemNo, this.httpOptions);
	}

	uploadAttachment(attachments: FormData): Observable<any> {
		return this.httpClient.post(this.attachmentsUrl, attachments, this.httpOptions);
	}

	downloadEdocsFile(docNum: number): Observable<any> {
		return this.httpClient.get(this.attachmentsUrl + '/downloadEdocsFile/' + docNum, { responseType: 'blob' as 'blob' });
	}

	downloadAzureFile(hash: string): Observable<any> {
		return this.httpClient.get(this.attachmentsUrl + '/downloadAzureFile/' + hash, { responseType: 'blob' as 'blob' });
	}

	getProfilePicture(token: string): Promise<any> {
		return this.httpClient
			.get(`${this.graphMeEndpoint}/me/photo/$value`, {
				headers: { Authorization: `Bearer ${token}` },
				responseType: 'blob',
			})
			.toPromise();
	}

	// downloadEdocsImage(docNum: number): Observable<any> {
	//   return this.httpClient.get(this.attachmentsUrl + '/downloadEdocsImage/'
	//     + docNum, { responseType: 'blob' as 'blob' });
	// }

	downloadEdocsImage(caseNum: string, docNums: string[]): Observable<EdocsDocument[]> {
		return this.httpClient.get<EdocsDocument[]>(this.attachmentsUrl + '/downloadEdocsImage', {
			params: {
				caseNo: caseNum,
				docNums: docNums,
			},
		});
	}

	deleteAttachment(attachment_Id: number): Observable<Attachment> {
		return this.httpClient.delete<Attachment>(this.attachmentsUrl + '/' + attachment_Id, this.httpOptions);
	}

	deleteContact(payload: ProblemContactsPayload): Observable<ProblemContact[]> {
		return this.httpClient.post<ProblemContact[]>(this.problemsUrl + '/delete-contact/' + payload.problem_Id, payload.contacts, this.httpOptions);
	}
	// deleteContact(contact_Id: number, problem_Id: number): Observable<ProblemContact[]> {
	//   return this.httpClient.delete<ProblemContact[]>(this.problemsUrl + '/contact/' + contact_Id + '/' + problem_Id, this.httpOptions);
	// }

	deletePhone(phone_Id: number, problem_Id: number): Observable<ContactPhone> {
		return this.httpClient.delete<ContactPhone>(this.problemsUrl + '/phone/' + phone_Id + '/' + problem_Id, this.httpOptions);
	}

	updateAttachment(attachment: Attachment): Observable<Attachment> {
		return this.httpClient.post<Attachment>(this.attachmentsUrl + '/updateAttachment', attachment, this.httpOptions);
	}

	problemRead(problemNo: string): Observable<number> {
		return this.httpClient.post<number>(this.problemsUrl + '/problemreadby/' + problemNo, this.httpOptions);
	}

	getAllLinkedCases(): Observable<LinkedCase[]> {
		return this.httpClient.get<LinkedCase[]>(this.problemsLinkedUrl, this.httpOptions);
	}

	getSearchListLinkedCases(caseNumbers: string[]): Observable<LinkedCase[]> {
		return this.httpClient.post<LinkedCase[]>(`${this.problemsLinkedUrl}/searchListLinkedCases`, caseNumbers, this.httpOptions);
	}

	getProblemLinkedCases(problemNo: string): Observable<LinkedCase[]> {
		return this.httpClient.get<LinkedCase[]>(this.problemsLinkedUrl + '/' + problemNo, this.httpOptions);
	}

	getProblemLinkedCasesAddMenu(caseNo: string, searchCaseNo: string): Observable<LinkedCaseRelated> {
		return this.httpClient.get<LinkedCaseRelated>(
			this.problemsLinkedUrl + '/relatedlinkcaseslookup/' + searchCaseNo + '/' + caseNo,
			this.httpOptions
		);
	}

	addProblemLinkedCase(linkedCasePayload: FromProblemLinkedCasePayload): Observable<LinkedCase[]> {
		return this.httpClient.post<LinkedCase[]>(this.problemsLinkedUrl + '/' + linkedCasePayload.caseNo, linkedCasePayload, this.httpOptions);
	}

	addProblemLinkedCases(linkedCasePayloads: LinkedCasePayload[]): Observable<LinkedCase[]> {
		return this.httpClient.post<LinkedCase[]>(this.problemsLinkedUrl + '/multiCaseLinking', linkedCasePayloads, this.httpOptions);
	}

	unlinkProblemCase(unlinkProblemCasePayload: UnlinkProblemCasePayload): Observable<LinkedCase[]> {
		return this.httpClient.delete<LinkedCase[]>(
			this.problemsLinkedUrl + '/' + unlinkProblemCasePayload.caseNo + '/' + unlinkProblemCasePayload.linkedCase_Id,
			this.httpOptions
		);
	}

	updateLinkedCaseNote(updateLinkedCasePayload: UpdateLinkedCasePayload): Observable<LinkedCase> {
		return this.httpClient.post<LinkedCase>(
			this.problemsLinkedUrl + '/' + updateLinkedCasePayload.caseNo + '/' + updateLinkedCasePayload.linkedCase_Id,
			updateLinkedCasePayload.updatedLinkedCase,
			this.httpOptions
		);
	}
}
